import React from "react";
import axios from "axios";
import { Volume2, Loader2, Square } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SpeakButton — sends text to /api/tts/speak (ElevenLabs multilingual v2)
 * and plays the returned MP3. Reuses cached blob URL for the same text.
 */
export default function SpeakButton({ text, voice = "rachel", className = "", testId = "speak-btn", label = "Listen" }) {
  const [state, setState] = React.useState("idle"); // idle | loading | playing
  const audioRef = React.useRef(null);
  const urlRef = React.useRef(null);

  React.useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    if (audioRef.current) audioRef.current.pause();
  }, []);

  const play = async () => {
    if (state === "playing") { audioRef.current?.pause(); setState("idle"); return; }
    if (urlRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play(); setState("playing"); return;
    }
    setState("loading");
    try {
      const res = await axios.post(`${API}/tts/speak`, { text, voice }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setState("idle");
      audio.onerror = () => { setState("idle"); toast.error("Playback failed"); };
      await audio.play();
      setState("playing");
    } catch (e) {
      setState("idle");
      toast.error(e.response?.data?.detail || "TTS failed");
    }
  };

  const Icon = state === "loading" ? Loader2 : state === "playing" ? Square : Volume2;

  return (
    <button
      data-testid={testId}
      onClick={play}
      disabled={state === "loading" || !text}
      className={`inline-flex items-center gap-1.5 rounded-full border transition-all ${
        state === "playing"
          ? "bg-[color:var(--forest)] border-[color:var(--forest)] text-white"
          : state === "loading"
          ? "bg-[color:var(--forest-soft)] border-[color:var(--forest)] text-[color:var(--forest)]"
          : "bg-white border-[color:var(--line-strong)] text-[color:var(--ink-2)] hover:border-[color:var(--forest)] hover:text-[color:var(--forest)]"
      } ${className}`}
      aria-label={label}
      title={label}
    >
      <Icon className={`w-3.5 h-3.5 ${state === "loading" ? "animate-spin" : ""}`} />
      <span className="text-[10px] font-semibold tracking-wide uppercase">{state === "playing" ? "Stop" : label}</span>
    </button>
  );
}
